/**
 * useLocaleConfig Hook
 *
 * React hook for managing project-level locale configuration.
 * Provides real-time subscription and mutation operations.
 *
 * Follows the useFontConfig pattern.
 *
 * @module locale-config/hooks
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import type { ProjectLocaleConfig, ProjectLocale } from '../types/localeConfig.types';
import { DEFAULT_LOCALE_CONFIG } from '../types/localeConfig.types';
import { getLocaleConfigService } from '../services/LocaleConfigService';

export interface UseLocaleConfigResult {
  /** Current locale configuration */
  config: ProjectLocaleConfig;
  /** Whether the canonical project locale document exists in Firestore. */
  configured: boolean;
  /** Loading state */
  loading: boolean;
  /** Error state */
  error: Error | null;
  /** Saving state */
  saving: boolean;

  /** Add a locale to the project */
  addLocale: (locale: ProjectLocale) => Promise<void>;
  /** Remove a locale by code */
  removeLocale: (code: string) => Promise<void>;
  /** Set the default locale (must already be in locales[]) */
  setDefaultLocale: (code: string) => Promise<void>;
  /** Replace the full locales list (for reordering) */
  setLocales: (locales: ProjectLocale[]) => Promise<void>;
  /** Reset to defaults */
  resetToDefaults: () => Promise<void>;
}

export function useLocaleConfig(projectId: string | undefined): UseLocaleConfigResult {
  const [config, setConfig] = useState<ProjectLocaleConfig>({ ...DEFAULT_LOCALE_CONFIG });
  const [configured, setConfigured] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [saving, setSaving] = useState(false);

  const service = useMemo(() => getLocaleConfigService(), []);

  // Subscribe to real-time updates
  useEffect(() => {
    if (!projectId) {
      setConfig({ ...DEFAULT_LOCALE_CONFIG });
      setConfigured(false);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const unsubscribe = service.subscribeToConfig(projectId, (newConfig, metadata) => {
      setConfig(newConfig);
      setConfigured(metadata.exists);
      setLoading(false);
    });

    return () => {
      unsubscribe();
    };
  }, [projectId, service]);

  const saveWithError = useCallback(async (newConfig: ProjectLocaleConfig) => {
    if (!projectId) throw new Error('No project ID');
    setSaving(true);
    try {
      await service.saveConfig(projectId, newConfig);
      setConfigured(true);
      setError(null);
    } catch (err) {
      const e = err instanceof Error ? err : new Error('Failed to save locale config');
      setError(e);
      throw e;
    } finally {
      setSaving(false);
    }
  }, [projectId, service]);

  const addLocale = useCallback(async (locale: ProjectLocale) => {
    if (config.locales.some(l => l.code === locale.code)) return;
    await saveWithError({
      ...config,
      locales: [...config.locales, locale],
    });
  }, [config, saveWithError]);

  const removeLocale = useCallback(async (code: string) => {
    if (code === config.defaultLocale) {
      throw new Error('Cannot remove the default locale');
    }
    await saveWithError({
      ...config,
      locales: config.locales.filter(l => l.code !== code),
    });
  }, [config, saveWithError]);

  const setDefaultLocale = useCallback(async (code: string) => {
    if (!config.locales.some(l => l.code === code)) {
      throw new Error(`Locale "${code}" is not in the project`);
    }
    await saveWithError({
      ...config,
      defaultLocale: code,
    });
  }, [config, saveWithError]);

  const setLocales = useCallback(async (locales: ProjectLocale[]) => {
    const defaultStillExists = locales.some(l => l.code === config.defaultLocale);
    await saveWithError({
      ...config,
      locales,
      defaultLocale: defaultStillExists ? config.defaultLocale : (locales[0]?.code ?? 'en-US'),
    });
  }, [config, saveWithError]);

  const resetToDefaults = useCallback(async () => {
    await saveWithError({ ...DEFAULT_LOCALE_CONFIG });
  }, [saveWithError]);

  return {
    config,
    configured,
    loading,
    error,
    saving,
    addLocale,
    removeLocale,
    setDefaultLocale,
    setLocales,
    resetToDefaults,
  };
}
